import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { markRecommendationFiltersAdjusted } from "../../stores/recommendations/mentorRecommendationsSlice";
import {
  filterCareersEventBuilder,
  sendEvent,
} from "../../utils/event-helpers";
import { useTaggedResourceTiles } from "../../components/ResourcePage/helpers/useTaggedResourceTiles";
import {
  filterResourceTileGroupsByQuery,
  filterResourceTileGroupsByTag,
} from "../../components/ResourcePage/helpers/filterResourceTileGroups";
import ResourcePageHeader from "../../components/ResourcePage/ResourcePageHeader";
import ResourceTilesGrid from "../../components/ResourcePage/ResourceTileGrid";
import { Search } from "../../components/Search/Search";
import ResourceFilterPopover from "../../components/FilterPopover/FilterPopover";
import { FilterPillRow } from "../../components/FilterPillRow/FilterPillRow";
import { SortPopover } from "../../components/SortPopover/SortPopover";
import { sortResourceTiles } from "../../utils/resourceSorting";
import type { RecommenderOutletContext } from "../RecommenderPage/recommenderPageTypes";

const CAREER_TAG_FILTERS = [
  "Career General Resources",
  "Career Additional Resources",
];

const ExploreCareers = () => {
  const dispatch = useAppDispatch();

  const { userId, sessionId } = useAppSelector((state) => state.session);

  const { pageFilters, setPageFilters, sortSelections, setSortSelections } =
    useOutletContext<RecommenderOutletContext>();

  const [query, setQuery] = useState<string>("");

  const recommendations = useAppSelector(
    (state) => state.mentorRecommendations.data,
  );

  const applyRecommendations =
    recommendations && !recommendations.recommendedFilters.adjusted;
  const selectedSubfields = applyRecommendations
    ? recommendations.recommendedFilters.subFields
    : pageFilters.exploreCareers.subfields;

  const taggedResourceTiles = useTaggedResourceTiles(CAREER_TAG_FILTERS);

  const filteredResourceTiles = useMemo(
    () =>
      filterResourceTileGroupsByTag(taggedResourceTiles, {
        subfields: selectedSubfields,
      }),
    [taggedResourceTiles, selectedSubfields],
  );

  const queriedResourceTiles = useMemo(
    () => filterResourceTileGroupsByQuery(filteredResourceTiles, query),
    [filteredResourceTiles, query],
  );

  const sortedResourceTiles = useMemo(
    () =>
      sortResourceTiles(queriedResourceTiles, sortSelections.exploreCareers),
    [queriedResourceTiles, sortSelections.exploreCareers],
  );

  const tileCount = sortedResourceTiles
    .map((group) => group.data.length)
    .reduce((acc, e) => acc + e, 0);

  const updateSubfields = useCallback(
    (subfields: string[]) => {
      setPageFilters((prev) => ({
        ...prev,
        exploreCareers: { subfields },
      }));
    },
    [setPageFilters],
  );

  const handleSubfieldsChange = useCallback(
    (subfields: string[]) => {
      dispatch(markRecommendationFiltersAdjusted());
      updateSubfields(subfields);
    },
    [dispatch, updateSubfields],
  );

  const handleClearFilters = useCallback(() => {
    dispatch(markRecommendationFiltersAdjusted());
    updateSubfields([]);
  }, [dispatch, updateSubfields]);

  useEffect(() => {
    if (!userId || !sessionId) return;

    const allFilteredTiles = filteredResourceTiles
      .map((tile) => tile.data)
      .flat();
    const allTileTitles = allFilteredTiles.map((tile) => tile.title);

    const eventData = filterCareersEventBuilder(
      userId,
      sessionId,
      allTileTitles,
      {
        subfieldsFilter: selectedSubfields,
      },
    );

    void dispatch(sendEvent(eventData));
  }, [userId, sessionId, selectedSubfields, filteredResourceTiles, dispatch]);

  return (
    <>
      <ResourcePageHeader
        title="Career Exploration Resources"
        tileCount={tileCount}
        style={{ marginBottom: "1rem" }}
      />
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <Search
          handleSearchChange={(e) => {
            setQuery(e.target.value);
          }}
          query={query}
        />
        <ResourceFilterPopover
          showSubfield
          selectedSubfields={selectedSubfields}
          onSubfieldsChange={handleSubfieldsChange}
          onClear={handleClearFilters}
        />
        <SortPopover
          option={sortSelections.exploreCareers}
          onChange={(option) => {
            setSortSelections({ ...sortSelections, exploreCareers: option });
          }}
        />
      </div>
      <FilterPillRow
        filters={selectedSubfields}
        onClear={(filter) => {
          handleSubfieldsChange(
            selectedSubfields.filter((value) => value !== filter),
          );
        }}
      />

      <div
        style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
        data-cy={"explore-careers-resources"}
      >
        {sortedResourceTiles.map((group) => (
          <ResourceTilesGrid
            key={group.tagFilter}
            resourceTiles={group.data}
            bookmarkSource="ExploreCareersTile"
          />
        ))}
      </div>
    </>
  );
};

export default ExploreCareers;
